import { Box, Typography } from "@mui/material";
import type { ProgressLegendItem } from "../../../../widgets/charts";
import type { ChartSeries } from "./dashboard-unified-chart";

// Inventory Chart Configuration
export const inventoryChartConfig = {
  title: "Stock Balances",
  barCategoryGap: "70%",
  series: [
    {
      dataKey: "inventory",
      label: "Inventory Value",
      fill: "#4B6F1D",
      color: "#6b7c5d",
      radius: [8, 8, 8, 0],
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Inventory Value
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            The wholesale value of unsold stock held on the platform, excluding
            expected production costs for unfinished items.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Inventory Value = (Purchase Price + Capitalised Expenses) * Number
            of Units
          </Typography>
        </Box>
      ),
    },
    {
      dataKey: "funded",
      label: "Funded Balance",
      fill: "#E2EDD0",
      color: "#c5d4b8",
      radius: [8, 8, 0, 8],
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Funded Inventory Balance
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            Total funds advanced to you against stock held on the platform,
            including accrued fees and any capitalised costs.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Funded Balance = (Purchase Price + Capitalised Expenses + Accrued
            Fees - Deposit) * Number of Units
          </Typography>
        </Box>
      ),
    },
  ] as ChartSeries[],
};

// Expiring Deal Chart Configuration
export const expiringDealChartConfig = {
  title: "Expiring Deals",
  titleTooltip: (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#000" }}>
        Expiring Deals (and Fees Due)
      </Typography>
      <Typography variant="body2" sx={{ color: "#333" }}>
        Each deal has an expiry date (compulsory sale, or final) by which stock
        must be sold or repurchased. Renewals may be possible with credit team
        approval.
      </Typography>
    </Box>
  ),
  barCategoryGap: "73%",
  series: [
    {
      dataKey: "repurchaseCost",
      label: "Repurchase Cost",
      fill: "#AD4E0B",
      color: "#AD4E0B",
      radius: [8, 8, 8, 0],
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Repurchase Cost
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            The amount payable to buy back any unsold stock from deals that have
            expired.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Repurchase Cost = (Purchase Price + Accrued Fees + Capitalised
            Expenses - Deposit Paid) * Number of Expiring Units
          </Typography>
        </Box>
      ),
    },
    {
      dataKey: "feesDue",
      label: "Fees Due",
      fill: "#FFDDC4",
      color: "#FFDDC4",
      radius: [8, 8, 0, 8],
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Fees Due
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            Accrued fees and capitalised costs payable for deals expiring this
            month. Applies only if a renewal is granted, as these amounts are
            always due upon renewal.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Fees = Throughput Fees + Monthly Fees + Capitalised Expenses
          </Typography>
        </Box>
      ),
    },
  ] as ChartSeries[],
};

// Sales Chart Configuration
export const salesChartConfig = {
  title: "Sales",
  barCategoryGap: "20%",
  maxBarSize: 18,
  series: [
    {
      dataKey: "customerSales",
      label: "Customer Sales",
      fill: "#445475",
      color: "#445475",
      radius: [0, 0, 8, 8],
      stackId: "sales",
      barSize: 18,
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Customer Sales
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            Total invoice value of sales made to your customers through the
            platform, excluding VAT and duty.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Customer Sales = Trade Sales Invoice Value - VAT - Duty
          </Typography>
        </Box>
      ),
    },
    {
      dataKey: "stockRepurchased",
      label: "Stock Repurchased",
      fill: "#CAD7EF",
      color: "#CAD7EF",
      radius: [8, 8, 0, 0],
      stackId: "sales",
      barSize: 18,
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Stock Repurchased
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            The value of stock you have bought back within a given month.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Stock Repurchased = Sale Order Invoice Value - VAT - Duty
          </Typography>
        </Box>
      ),
    },
  ] as ChartSeries[],
};

// Sales Credit Limit Chart Configuration
export const salesCreditLimitChartConfig = {
  title: "Sales Credit Limit",
  titleTooltip: (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#000" }}>
        Sales Credit Limit
      </Typography>
      <Typography variant="body2" sx={{ color: "#333" }}>
        This is the maximum credit available for placing trade sales or sales
        orders without upfront payment. It enables you to defer payment until a
        later date, rather than paying at the time of order. Any invoices issued
        to recharge costs incurred by Ferovinum will also be charged to this
        balance.
      </Typography>
    </Box>
  ),
  progressColors: {
    used: "#793A3A",
    remaining: "#FBD9D9",
  },
  legends: [
    {
      key: "used",
      label: "Used",
      color: "#793A3A",
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Used Sales Credit
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            The used portion of your total sales credit limit tied to trade
            sales or sales orders where this was used as the settlement option.
            Any overdue invoices must be paid down.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Used SC = Outstanding invoices + Released Sale Orders (settled with
            SC) + Trade Sales (settled with your SC)
          </Typography>
        </Box>
      ),
    },
    {
      key: "remaining",
      label: "Remaining",
      color: "#FBD9D9",
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#000" }}
          >
            Remaining Sales Credit
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#333" }}>
            The amount of credit you still have available to use for new trade
            sales or sales orders.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "#666" }}
          >
            Remaining Sales Credit = Total Sales Credit - Used Sales Credit
          </Typography>
        </Box>
      ),
    },
  ] as ProgressLegendItem[],
};
